import React, { useState, useEffect } from 'react';
import { ClipboardCheck, TrendingUp, TrendingDown, AlertTriangle, ChevronRight } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useNavigate } from 'react-router-dom';

export default function StoreAuditWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiRequest('/store-ops/audit/widget');
        setData(result);
      } catch (error) {
        console.error('Failed to load audit data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Card data-testid="store-audit-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Store Audits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-8 bg-slate-200 rounded w-full"></div>
            <div className="h-8 bg-slate-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const storeRankings = data?.store_rankings || [];
  const actionItems = data?.pending_action_items || 0;

  return (
    <Card data-testid="store-audit-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-purple-600" />
            Store Walk Audits
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/store-audits')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Store Rankings */}
        {storeRankings.length > 0 ? (
          <div className="space-y-3">
            <div className="text-xs font-medium text-slate-500 uppercase">Store Scores</div>
            {storeRankings.slice(0, 5).map((store, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium flex items-center gap-2">
                    {store.store}
                    {store.trend === 'up' && <TrendingUp className="h-3 w-3 text-green-500" />}
                    {store.trend === 'down' && <TrendingDown className="h-3 w-3 text-red-500" />}
                  </span>
                  <span className={`font-bold ${
                    store.average_score >= 80 ? 'text-green-600' : 
                    store.average_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {store.average_score}%
                  </span>
                </div>
                <Progress 
                  value={store.average_score} 
                  className="h-2"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500">
            <ClipboardCheck className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No audits recorded yet</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={() => navigate('/admin/store-audits/new')}
            >
              Start Audit
            </Button>
          </div>
        )}

        {/* Action Items */}
        {actionItems > 0 && (
          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
            <span className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Pending Action Items
            </span>
            <Badge className="bg-yellow-100 text-yellow-800">{actionItems}</Badge>
          </div>
        )}

        {/* Recent Audits */}
        {(data?.recent_audits || []).length > 0 && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-medium text-slate-500 mb-2">Recent Audits</div>
            {data.recent_audits.slice(0, 2).map((audit, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs py-1">
                <span>{audit.store} - {audit.date}</span>
                <Badge variant="outline">{audit.overall_percentage}%</Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
